import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  signal,
  viewChildren
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { UserOptionsEnum } from '@appTypes/common.types';
import { authLogOut } from '@core/modules/auth/state/auth.actions';
import { AuthState } from '@core/modules/auth/state/auth.state';
import { UserState } from '@core/modules/user/state/user.state';
import { LANGUAGE_SELECT_OPTIONS } from '@data/locale.data';
import { HEADER_MENU, USER_OPTIONS_MENU } from '@data/menu.data';
import { environment } from '@env/environment';
import { TranslatePipe } from '@ngx-translate/core';
import { Navigate } from '@ngxs/router-plugin';
import { createDispatchMap, createSelectMap } from '@ngxs/store';
import { LogoComponent } from '@shared/components/logo/logo.component';
import { SelectorComponent } from '@shared/components/selector/selector.component';
import { CartModule } from '@shared/modules/cart/cart.module';
import { ChatModule } from '@shared/modules/chat/chat.module';
import { DynamicOverlayModule } from '@shared/modules/dynamic-overlay/dynamic-overlay.module';
import {
  appGetCruiseAvailability,
  appGetExcursionDestinations,
  appSetCurrency,
  appSetLanguage
} from '../../../state/app.actions';
import { AppPersistedState } from '../../../state/app.persisted.state';
import { AppState } from '../../../state/app.state';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [
    MatIconModule,
    MatButtonModule,
    LogoComponent,
    RouterModule,
    TranslatePipe,
    CartModule,
    ChatModule,
    SelectorComponent,
    DynamicOverlayModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements AfterViewInit {
  readonly menuItemRef = viewChildren<ElementRef<HTMLLinkElement>>('menuItemRef');

  readonly selectors = createSelectMap({
    currencies: AppState.currencies,
    currenciesLoadState: AppState.currenciesLoadState,
    activedCurrency: AppPersistedState.activedCurrency,
    activedLanguage: AppPersistedState.language,
    userName: UserState.userName,
    isAuthenticated: AuthState.isAuthenticated,
    sellsExcursions: AppState.sellsExcursions,
    sellsCruises: AppState.sellsCruises
  });

  public readonly languages = LANGUAGE_SELECT_OPTIONS;

  //Temporary solution
  public readonly isChatEnabled = !!environment?.urls?.chatApi;

  readonly #actions = createDispatchMap({
    setCurrency: appSetCurrency,
    setLanguage: appSetLanguage,
    logOut: authLogOut,
    navigate: Navigate,
    getExcursionDestinations: appGetExcursionDestinations,
    getCruiseAvailability: appGetCruiseAvailability
  });

  readonly desktopDefaultPagePath: string = environment.desktopDefaultPagePath;
  /**
   * TravelDesk P-18: the Excursions tab is offered only when a destination is sold as "Excursions only" in the Destination Module.
   * Cruises phase 4 (R-47): the Cruises tab only when the agency's market is on sale on a cruise channel today (QA doc features/cruises/qa.md).
   */
  readonly headerMenu = computed(() =>
    HEADER_MENU.filter(
      (m) =>
        (m.path !== './excursions' || this.selectors.sellsExcursions()) &&
        (m.path !== './cruises' || this.selectors.sellsCruises())
    )
  );
  readonly userMenuOptions = USER_OPTIONS_MENU;

  constructor() {
    // a new language or a tab shown / hidden changes the tabs' widths: measure the active one again once it is drawn
    effect(() => {
      this.selectors.activedLanguage();
      this.headerMenu();
      setTimeout(() => this.#fixIndicatorWidth(), 100);
    });
    effect(() => {
      if (this.selectors.isAuthenticated()) {
        this.#actions.getExcursionDestinations();
        this.#actions.getCruiseAvailability();
      }
    });
  }

  get onSetLanguage() {
    return this.#actions.setLanguage;
  }

  /**
   * Applies a currency picked from the header selector.
   *
   * The selector settles its value against the option list once loading ends, and emits `undefined`
   * when the current value is not in it — which is exactly what a failed `GetCurrencies` leaves
   * behind, an empty list. Persisting that would drop the agent's currency and, through
   * `appSetCurrency`, fire a cart recalculation off the back of an unrelated failure.
   */
  onCurrencySelect(currency?: string | null) {
    if (!currency) return;
    this.#actions.setCurrency(currency);
  }

  /**
   * R-CAL21 (website-web#166, Amiran 30.10.2026 "Agent's click wins"): every click (or Enter) on a currency in the header list is the agent's
   * own choice - also on the value already shown, which `valueChange` never reports. It is handed to the store as a pick, so a late agency
   * default (R-CAL19) can no longer move the header or the checkout.
   */
  onCurrencyPicked(option?: { code?: string | null } | null) {
    if (!option?.code) return;
    this.#actions.setCurrency(option.code, true);
  }
  active?: number;
  readonly indicatorWidth = signal('5px');
  /** The active tab's own left edge: the tabs are as wide as their labels ("Excursions", the Russian labels), so an index x 70 px offset drifted. */
  readonly indicatorOffset = signal('0px');

  ngAfterViewInit(): void {
    setTimeout(() => this.#fixIndicatorWidth(), 100);
  }

  onUserMenuSelect(selected: (typeof USER_OPTIONS_MENU)[0]['value']) {
    if (selected === UserOptionsEnum.logOut) this.#actions.logOut();
    else if (selected === UserOptionsEnum.changePassword) this.#actions.navigate(['/change-password']);
  }

  swipeRoute(direction: 'right' | 'left') {
    if (this.active !== undefined) {
      const newIndex = direction === 'right' ? this.active + 1 : this.active - 1;
      if (this.headerMenu()[newIndex]?.path) {
        this.#actions.navigate([this.headerMenu()[newIndex].path]);
      }
    }
  }
  onActiveChange(event: boolean, index: number) {
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (event) {
      case true:
        if (this.active !== index) {
          this.active = index;
          this.#fixIndicatorWidth();
        }
        break;
      default:
        if (index === this.active) this.active = undefined;
        break;
    }
  }
  #fixIndicatorWidth() {
    const activedElement = this.menuItemRef()?.at(this.active ?? 0)?.nativeElement;
    if (!activedElement) return;
    this.indicatorWidth.set(`${Math.max(5, activedElement.clientWidth - 31 * 2)}px`);
    this.indicatorOffset.set(`${activedElement.parentElement?.offsetLeft ?? 0}px`);
  }
}
